/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import React from 'react';
import { Button, IconButton } from '@mui/material';
import { CallDirection, CallStatus } from '../typings';
import { ReactComponent as MessageIconV2 } from '../../../shared/assets/svg/messagev2.svg';
import { ReactComponent as MessageIconV3 } from '../../../shared/assets/svg/messagev3.svg';
import { ReactComponent as MuteIcon } from '../../../shared/assets/svg/mute.svg';
import PhoneOutlined from '../../../shared/icons/PhoneOutlined';
import CloseIcon from '../../../shared/icons/Close';
import { ActionContainer, commonButtonStyles, commonIconButtonStyles } from '../styles';
import { useSubscribedPlan } from '../../renew-plan/v1/hooks/use-subscribed-plan';
import { PlanAddonTypes } from '../../renew-plan/v1/typings';
import { useSetAtom } from 'jotai';
import { waVoiceUpgradeModalOpen } from '../../calls/v1/states';

interface CallActionProps {
  callState: CallStatus;
  direction?: CallDirection;
  onAnswer: (switchCall?: boolean) => void;
  onReject?: () => void;
  onMuteToggle?: () => void;
  onSpeakerToggle?: () => void;
  onChatClick?: () => void;
  onClose?: () => void;
  onCallback?: () => void;
  onAddNote?: () => void;
  isMuted?: boolean;
  isSpeakerOn?: boolean;
  switchCall?: boolean;
}

export const CallAction: React.FC<CallActionProps> = ({
  callState,
  direction,
  onAnswer,
  onReject,
  onMuteToggle,
  onSpeakerToggle,
  onChatClick,
  onClose,
  onCallback,
  onAddNote,
  isMuted,
  isSpeakerOn,
  switchCall,
}) => {

  const { isAddonPresent } = useSubscribedPlan();
  const isWaVoiceAvailable = isAddonPresent(PlanAddonTypes.WaVoiceAddon);
  const setWaVoiceUpgradeModalOpen = useSetAtom(waVoiceUpgradeModalOpen);
  const handleAnswer = (switchCall?: boolean) => {
    if(isWaVoiceAvailable) {
      onAnswer(switchCall);
    } else {
      setWaVoiceUpgradeModalOpen(true);
    }
  }

  if (callState === CallStatus.INITIATED && direction !== CallDirection.OUTGOING) {
    return (
      <ActionContainer>
        <IconButton
          onClick={onChatClick}
          sx={{
            ...commonIconButtonStyles,
          }}
        >
          <MessageIconV2 width={20} height={20} color="#2d2d2d" />
        </IconButton>
        <Button
          variant="contained"
          color="success"
          startIcon={<PhoneOutlined color="white" width={20} height={20} />}
          onClick={() => handleAnswer(switchCall)}
          disableElevation={true}
          sx={{
            ...commonButtonStyles,
            backgroundColor: '#3D906D',
            ':hover': {
              backgroundColor: '#2c7254',
            },
          }}
        >
          {switchCall ? 'Switch' : 'Answer'}
        </Button>
        <IconButton
          onClick={onReject}
          sx={{
            ...commonIconButtonStyles,
          }}
        >
          <PhoneOutlined
            width={20}
            height={20}
            color="#EF5350"
            style={{
              transform: 'rotate(134deg)',
            }}
          />
        </IconButton>
      </ActionContainer>
    );
  }

  if (callState === CallStatus.INITIATED && direction === CallDirection.OUTGOING) {
    return (
      <ActionContainer>
        <Button
          variant="outlined"
          startIcon={<MessageIconV3 color="#7a7a7a" />}
          onClick={onChatClick}
          sx={{
            ...commonButtonStyles,
            color: '#2D2D2D',
            borderColor: '#E0E1E4',
            ':hover': {
              backgroundColor: '#F5F5F5',
              borderColor: '#E0E1E4',
            },
            '&:disabled': {
              backgroundColor: '#F5F5F5',
              color: '#7a7a7a',
            },
          }}
        >
          Message
        </Button>
        <IconButton
          onClick={onReject}
          sx={{
            ...commonButtonStyles,
            backgroundColor: '#EF5350',
            width: 'auto',
            flex: 1,
            ':hover': {
              backgroundColor: '#da4947',
            },
            '&:disabled': {
              backgroundColor: '#EF535099',
            },
          }}
        >
          <PhoneOutlined width={24} height={24} color="white" style={{ transform: 'rotate(134deg)' }} />
        </IconButton>
      </ActionContainer>
    );
  }

  if (callState === CallStatus.Connecting || callState === CallStatus.Ringing || callState === CallStatus.IN_PROGRESS) {
    return (
      <ActionContainer>
        {/* <IconButton onClick={onAddNote}>
          <NoteAddIcon />
        </IconButton> */}
        <IconButton
          onClick={onChatClick}
          sx={{
            ...commonIconButtonStyles,
            ':hover': {
              backgroundColor: '#ECECEC',
            },
          }}
        >
          <MessageIconV2 width={20} height={20} color="#2d2d2d" />
        </IconButton>
        <IconButton
          onClick={onMuteToggle}
          sx={{
            ...commonIconButtonStyles,
            backgroundColor: isMuted ? '#2d2d2d' : '#ffffff',
            ':hover': {
              backgroundColor: isMuted ? '#373737' : '#ECECEC',
            },
          }}
        >
          <MuteIcon color={isMuted ? '#ffffff' : '#2d2d2d'} />
        </IconButton>
        <IconButton
          onClick={onReject}
          sx={{
            ...commonButtonStyles,
            backgroundColor: '#EF5350',
            width: 'auto',
            flex: 1,
            ':hover': {
              backgroundColor: '#da4947',
            },
            '&:disabled': {
              backgroundColor: '#EF535099',
            },
          }}
        >
          <PhoneOutlined width={24} height={24} color="white" style={{ transform: 'rotate(134deg)' }} />
        </IconButton>
      </ActionContainer>
    );
  }

  if (callState === CallStatus.END) {
    return (
      <ActionContainer>
        <Button
          variant="outlined"
          startIcon={<MessageIconV3 color="#7a7a7a" />}
          onClick={onChatClick}
          sx={{
            ...commonButtonStyles,
            color: '#2D2D2D',
            fontWeight: 500,
            borderColor: '#E0E1E4',
            height: '48px',
            ':hover': {
              backgroundColor: '#F5F5F5',
              borderColor: '#E0E1E4',
            },
            '&:disabled': {
              backgroundColor: '#F5F5F5',
              color: '#7a7a7a',
            },
          }}
        >
          Message
        </Button>
        <Button
          variant="outlined"
          startIcon={<CloseIcon color="#7a7a7a" size={16} />}
          onClick={onClose}
          sx={{
            ...commonButtonStyles,
            color: '#2d2d2d',
            borderColor: '#E0E1E4',
            backgroundColor: 'transparent',
            overflow: 'hidden',
            ':hover': {
              backgroundColor: 'transparent',
              borderColor: '#E0E1E4',
            },
            '&:disabled': {
              backgroundColor: 'transparent',
              color: '#7a7a7a',
            },
            ':before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              width: '50%',
              height: '100%',
              backgroundColor: '#D9D9D9',
              animation: 'fillIn 3s linear forwards',
              zIndex: -1,
            },
            '@keyframes fillIn': {
              from: {
                width: '0%',
              },
              to: {
                width: '100%',
              },
            },
          }}
        >
          Close
        </Button>
      </ActionContainer>
    );
  }

  if (callState === CallStatus.MISSED || callState === CallStatus.FAILED) {
    return (
      <ActionContainer>
        <Button
          variant="outlined"
          startIcon={<MessageIconV3 color="#7a7a7a" />}
          onClick={onChatClick}
          sx={{
            ...commonButtonStyles,
            color: '#2d2d2d',
            borderColor: '#E0E1E4',
            ':hover': {
              backgroundColor: '#F5F5F5',
              borderColor: '#E0E1E4',
            },
            '&:disabled': {
              backgroundColor: '#F5F5F5',
              color: '#7a7a7a',
            },
          }}
        >
          Message
        </Button>
        <Button
          variant="outlined"
          startIcon={<CloseIcon color="#7a7a7a" size={16} />}
          onClick={onClose}
          sx={{
            ...commonButtonStyles,
            color: '#2d2d2d',
            borderColor: '#E0E1E4',
            backgroundColor: 'transparent',
            overflow: 'hidden',
            ':hover': {
              backgroundColor: 'transparent',
              borderColor: '#E0E1E4',
            },
            '&:disabled': {
              backgroundColor: 'transparent',
              color: '#7a7a7a',
            },
            ':before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              width: '50%',
              height: '100%',
              backgroundColor: '#D9D9D9',
              animation: 'fillIn 3s linear forwards',
              zIndex: -1,
            },
            '@keyframes fillIn': {
              from: {
                width: '0%',
              },
              to: {
                width: '100%',
              },
            },
          }}
        >
          Close
        </Button>
      </ActionContainer>
    );
  }

  return null;
};
